import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ShoppingCart, Tag, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getImageUrl } from "@/utils/imageUrl";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Products = () => {
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");

  const categories = [
    { id: "all", label: "Tous les produits" },
    { id: "calculateur-moteur", label: "Calculateur moteur" },
    { id: "boitier-habitacle", label: "Boitier habitacle" },
    { id: "boitier-fusible", label: "Boitier fusible" },
    { id: "calculateur-airbag", label: "Calculateur airbag" },
    { id: "bloc-abs", label: "Bloc ABS" },
    { id: "serrurerie", label: "Serrurerie" },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, settingsRes] = await Promise.all([
          axios.get(`${API}/products`),
          axios.get(`${API}/settings`)
        ]);
        setProducts(productsRes.data);
        setSettings(settingsRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredProducts = selectedCategory === "all" 
    ? products 
    : products.filter(p => p.categorie === selectedCategory);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
    }).format(price);
  };

  return (
    <div data-testid="products-page">
      {/* Hero */}
      <section className="py-20 bg-gray-50">
        <div className="page-container">
          <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            {(settings?.produits_titre || "NOS PRODUITS").split(" ")[0]} <span className="text-yellow-500">{(settings?.produits_titre || "NOS PRODUITS").split(" ").slice(1).join(" ") || "PRODUITS"}</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl">
            {settings?.produits_description || "Pièces électroniques automobiles d'occasion, testées et garanties."}
          </p>
        </div>
      </section>

      {/* Categories Filter */}
      <section className="py-6 bg-white border-b border-gray-200 sticky top-16 z-30">
        <div className="page-container">
          <div className="flex gap-2 overflow-x-auto pb-2">
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  selectedCategory === cat.id
                    ? 'bg-yellow-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="section-spacing bg-gray-50">
        <div className="page-container">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="bg-white rounded-xl h-80 animate-pulse" />
              ))}
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-16">
              <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Aucun produit dans cette catégorie</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {filteredProducts.map((product, index) => (
                <div 
                  key={product.id}
                  className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg hover:border-yellow-400 transition-all group animate-fade-in-up"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  {/* Image */}
                  <div className="relative aspect-square overflow-hidden bg-gray-100">
                    <div className="w-full h-full flex items-center justify-center bg-gray-200">
                      <span className="text-gray-500 text-sm font-medium text-center px-4">Non disponible pour le moment</span>
                    </div>
                    {!product.en_stock && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <span className="bg-red-500 text-white px-4 py-2 rounded-full text-sm font-medium">
                          Rupture de stock
                        </span>
                      </div>
                    )}
                    {product.prix_promo && (
                      <div className="absolute top-3 left-3 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                        PROMO
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className="font-semibold text-gray-900 line-clamp-2">{product.nom}</h3>
                    </div>
                    
                    {product.reference && (
                      <p className="text-xs text-gray-400 mb-2">Réf: {product.reference}</p>
                    )}

                    <p className="text-gray-600 text-sm line-clamp-2 mb-4">{product.description}</p>

                    <div className="flex items-center justify-between">
                      <div>
                        {product.prix_promo ? (
                          <div className="flex items-center gap-2">
                            <span className="text-xl font-bold text-red-500">{formatPrice(product.prix_promo)}</span>
                            <span className="text-sm text-gray-400 line-through">{formatPrice(product.prix)}</span>
                          </div>
                        ) : (
                          <span className="text-xl font-bold text-yellow-600">{formatPrice(product.prix)}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gray-900">
        <div className="page-container text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Vous ne trouvez pas ce que vous cherchez ?
          </h2>
          <p className="text-gray-400 mb-8 max-w-xl mx-auto">
            Contactez-nous pour une recherche personnalisée. Nous avons accès à un large réseau de fournisseurs.
          </p>
          <Link to="/contact">
            <Button className="bg-yellow-500 text-white hover:bg-yellow-600 font-bold uppercase">
              Nous contacter
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Products;
